import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Chip,
  Tooltip,
  InputAdornment,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocalShipping as ShippingIcon,
  Payment as PaymentIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import { db } from '../../firebase/firebase';

const BookingManagement = () => {
  // State management
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedBookingDetails, setSelectedBookingDetails] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [statusUpdateDialog, setStatusUpdateDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newPaymentStatus, setNewPaymentStatus] = useState('');
  const [error, setError] = useState(null);
  const [fortuneTellers, setFortuneTellers] = useState({});
  const [detailsTabValue, setDetailsTabValue] = useState(0);
  const [showRawData, setShowRawData] = useState(false);

  // Booking status options
  const statusOptions = [
    { value: 'pending', label: '待确认', color: 'warning' },
    { value: 'confirmed', label: '已确认', color: 'info' },
    { value: 'completed', label: '已完成', color: 'success' },
    { value: 'cancelled', label: '已取消', color: 'error' },
  ];

  // Payment status options
  const paymentStatusOptions = [
    { value: 'pending', label: '待付款', color: 'warning' },
    { value: 'completed', label: '已付款', color: 'success' },
    { value: 'refunded', label: '已退款', color: 'error' },
  ];

  // Load bookings data
  const loadBookings = async (reset = false) => {
    try {
      setLoading(reset);
      if (reset) {
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
      }

      let bookingsQuery = collection(db, 'bookings');
      let constraints = [];

      if (statusFilter !== 'all') {
        // 处理状态过滤
        if (statusFilter === 'paid') {
          constraints.push(where('paymentStatus', '==', 'completed'));
        } else if (statusFilter === 'unpaid') {
          constraints.push(where('paymentStatus', '==', 'pending'));
        } else {
          constraints.push(where('status', '==', statusFilter));
        }
      }

      constraints.push(orderBy('date', 'desc'));
      constraints.push(limit(10));

      if (lastVisible && !reset) {
        constraints.push(startAfter(lastVisible));
      }

      const q = query(bookingsQuery, ...constraints);
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setHasMore(false);
        setLoadingMore(false);
        setLoading(false);
        return;
      }

      const newLastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
      setLastVisible(newLastVisible);

      const newBookings = await Promise.all(querySnapshot.docs.map(async doc => {
        const bookingData = doc.data();
        
        console.log(`预约 ${doc.id} 完整数据:`, JSON.stringify(bookingData, null, 2));
        
        // 提取所有可能的客户联系信息 - 使用函数来安全访问嵌套属性
        const safeGet = (obj, path, defaultValue = null) => {
          if (!obj) return defaultValue;
          return path.split('.').reduce((prev, curr) => {
            return prev && prev[curr] !== undefined ? prev[curr] : defaultValue;
          }, obj);
        };
        
        // 获取客户姓名 - 尝试多个可能的路径
        let customerName = bookingData.customerName || 
                          safeGet(bookingData, 'userInfo.displayName') ||
                          safeGet(bookingData, 'paymentSource.name.given_name') ||
                          safeGet(bookingData, 'paymentSource.name.full') ||
                          safeGet(bookingData, 'payer.name.given_name') ||
                          safeGet(bookingData, 'payer.name.surname') ||
                          safeGet(bookingData, 'customer.name') ||
                          safeGet(bookingData, 'user.displayName') ||
                          safeGet(bookingData, 'name') || // 直接从预约数据中获取
                          '预约客户';
        
        // 获取客户邮箱 - 尝试多个可能的路径                
        let customerEmail = bookingData.customerEmail || 
                           safeGet(bookingData, 'userInfo.email') ||
                           safeGet(bookingData, 'userEmail') || // 用户提供的邮箱
                           safeGet(bookingData, 'paymentSource.email_address') || // PayPal支付源邮箱
                           safeGet(bookingData, 'payer.email_address') || // PayPal付款人邮箱
                           safeGet(bookingData, 'customer.email') ||
                           safeGet(bookingData, 'user.email') ||
                           safeGet(bookingData, 'email') ||
                           '';
                           
        // 获取客户电话号码 - 尝试多个可能的路径
        let customerPhone = bookingData.customerPhone || 
                           safeGet(bookingData, 'userInfo.phone') ||
                           safeGet(bookingData, 'phone') ||
                           safeGet(bookingData, 'phoneNumber') ||
                           safeGet(bookingData, 'customerPhoneNumber') ||
                           safeGet(bookingData, 'customer.phone') ||
                           safeGet(bookingData, 'user.phoneNumber') ||
                           safeGet(bookingData, 'user.phone') ||
                           '';
        
        // 确保我们有价格信息 - 尝试所有可能的字段
        let price = bookingData.price || 
                   bookingData.amount || 
                   bookingData.cost || 
                   safeGet(bookingData, 'payment.amount') ||
                   safeGet(bookingData, 'paymentDetails.amount') ||
                   safeGet(bookingData, 'service.price') ||
                   0;
        
        // 获取支付状态 - 尝试多个可能的路径
        let paymentStatus = bookingData.paymentStatus || 
                           safeGet(bookingData, 'paymentSource.status') ||
                           safeGet(bookingData, 'payment.status') ||
                           safeGet(bookingData, 'paymentDetails.status') ||
                           safeGet(bookingData, 'paymentDetails.captureDetails.status') ||
                           'pending';
        
        // 获取命理师名称 - 尝试多个可能的路径
        let fortuneTellerName = bookingData.fortuneTellerName ||
                               safeGet(bookingData, 'service.providerName') ||
                               safeGet(bookingData, 'provider.name') ||
                               safeGet(bookingData, 'fortuneTeller.name') ||
                               '';
        
        // 获取用户数据（如果有）
        let userData = null;
        if (bookingData.userId) {
          try {
            const userDoc = await getDoc(doc(db, 'users', bookingData.userId));
            if (userDoc.exists()) {
              userData = userDoc.data();
              
              // 如果我们仍然没有客户姓名，尝试从用户数据获取
              if (!customerName && userData.displayName) {
                customerName = userData.displayName;
              }
              
              // 如果我们仍然没有邮箱信息，尝试从用户数据获取
              if (!customerEmail && userData.email) {
                customerEmail = userData.email;
              }
              
              // 如果我们仍然没有电话信息，尝试从用户数据获取
              if (!customerPhone && (userData.phoneNumber || userData.phone)) {
                customerPhone = userData.phoneNumber || userData.phone;
              }
            }
          } catch (error) {
            console.error(`获取用户数据失败: ${error.message}`);
          }
        }

        // 提取其他可能有用的信息
        let paymentDetails = bookingData.paymentDetails || null;
        let paymentSource = bookingData.paymentSource || null;
        let paymentMethod = bookingData.paymentMethod || 
                            safeGet(bookingData, 'payment.method') || 
                            (paymentSource ? 'PayPal' : null);
        
        // 服务器端支付的特殊处理
        if (bookingData.paymentMethod === 'paypal_server') {
            paymentMethod = 'PayPal(服务器)';
        }
        let paymentDate = bookingData.paymentDate || 
                          bookingData.paymentTime || 
                          safeGet(bookingData, 'payment.date') || 
                          safeGet(bookingData, 'paymentDetails.update_time') || 
                          safeGet(bookingData, 'paymentDetails.captureDetails.update_time') || 
                          null;
        let transactionId = bookingData.transactionId || 
                            bookingData.paymentCaptureId || 
                            bookingData.paypalOrderId || 
                            safeGet(bookingData, 'payment.id') || 
                            safeGet(bookingData, 'paymentDetails.id') || 
                            safeGet(bookingData, 'paymentDetails.captureDetails.id') || 
                            null;
        let paymentId = bookingData.paymentId || transactionId || null;
        let serviceInfo = bookingData.service || null;
        let duration = bookingData.duration || 
                       safeGet(bookingData, 'service.duration') || 
                       null;
                       
        // 获取问题和回复内容
        let questions = bookingData.questions || [];
        let response = bookingData.response || null;

        return {
          id: doc.id,
          ...bookingData,
          date: bookingData.date?.toDate?.() || new Date(bookingData.date),
          userData,
          customerName: customerName || null,
          customerEmail: customerEmail || null,
          customerPhone: customerPhone || null,
          price: price,
          fortuneTellerName: fortuneTellerName || null,
          paymentStatus: paymentStatus,
          paymentDetails: paymentDetails,
          paymentSource: paymentSource,
          paymentMethod: paymentMethod,
          paymentDate: paymentDate,
          transactionId: transactionId,
          paymentId: paymentId,
          service: serviceInfo,
          duration: duration,
          questions: questions,
          response: response
        };
      }));

      // 获取所有相关的占卜师数据
      const fortuneTellerIds = [...new Set(newBookings.map(booking => booking.fortuneTellerId))];
      
      if (fortuneTellerIds.length > 0) {
        const fortuneTellersData = {};
        
        for (const id of fortuneTellerIds) {
          if (id) {
            try {
              const fortuneTellerDoc = await getDoc(doc(db, 'fortuneTellers', id));
              if (fortuneTellerDoc.exists()) {
                fortuneTellersData[id] = {
                  id: fortuneTellerDoc.id,
                  ...fortuneTellerDoc.data()
                };
              }
            } catch (err) {
              console.error(`获取占卜师数据失败: ${err.message}`);
            }
          }
        }
        
        setFortuneTellers(fortuneTellersData);
        
        // 对于没有价格的预约，从占卜师数据中补充价格信息
        for (const booking of newBookings) {
          // 1. 检查预约是否已有价格信息
          if ((!booking.price || booking.price === 0) &&
              (!booking.amount || booking.amount === 0) &&
              (!booking.cost || booking.cost === 0)) {
            
            // 2. 从占卜师数据中获取价格
            if (booking.fortuneTellerId && fortuneTellersData[booking.fortuneTellerId]) {
              const tellerPrice = fortuneTellersData[booking.fortuneTellerId].price;
              if (tellerPrice && tellerPrice > 0) {
                console.log(`为预约 ${booking.id} 设置价格 ${tellerPrice} (从占卜师数据)`);
                booking.price = tellerPrice;
                
                // 3. 尝试更新数据库中的记录，添加价格字段
                try {
                  // 使用非阻塞方式更新数据库中的价格信息 - 不等待结果
                  updateDoc(doc(db, 'bookings', booking.id), { 
                    price: tellerPrice,
                    updatedAt: serverTimestamp(),
                    priceUpdatedFromTeller: true // 标记价格来源
                  }).then(() => {
                    console.log(`成功更新预约 ${booking.id} 的价格信息`);
                  }).catch(err => {
                    console.error(`更新预约 ${booking.id} 价格信息失败:`, err);
                  });
                } catch (err) {
                  console.error(`处理预约 ${booking.id} 价格更新时出错:`, err);
                }
              }
            }
          }
        }
      }

      if (reset) {
        setBookings(newBookings);
      } else {
        setBookings(prev => [...prev, ...newBookings]);
      }

      setHasMore(querySnapshot.docs.length === 10);
    } catch (error) {
      console.error('加载预约失败:', error);
      setError('加载预约失败');
      setSnackbar({
        open: true,
        message: '加载预约失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Initial load and when filter changes
  useEffect(() => {
    loadBookings(true);
  }, [statusFilter]);

  // Handle search
  const handleSearch = () => {
    if (searchTerm.trim() !== '') {
      const filtered = bookings.filter(
        booking => 
          booking.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (booking.userData?.displayName && booking.userData.displayName.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (booking.userData?.email && booking.userData.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (booking.userData?.phoneNumber && booking.userData.phoneNumber.includes(searchTerm)) ||
          (booking.customerPhone && booking.customerPhone.includes(searchTerm)) ||
          (booking.topic && booking.topic.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setBookings(filtered);
      setHasMore(false);
    } else {
      loadBookings(true);
    }
  };

  // View booking details
  const handleViewBooking = async (id) => {
    try {
      setLoading(true);
      const bookingDoc = await getDoc(doc(db, 'bookings', id));
      
      if (bookingDoc.exists()) {
        const bookingData = bookingDoc.data();
        console.log(`查看预约 ${id} 详情:`, JSON.stringify(bookingData, null, 2));
        
        // 使用safeGet函数安全访问嵌套属性
        const safeGet = (obj, path, defaultValue = null) => {
          if (!obj) return defaultValue;
          return path.split('.').reduce((prev, curr) => {
            return prev && prev[curr] !== undefined ? prev[curr] : defaultValue;
          }, obj);
        };
        
        // 补充处理客户信息
        if (!bookingData.customerName) {
          bookingData.customerName = safeGet(bookingData, 'userInfo.displayName') ||
                                    safeGet(bookingData, 'paymentSource.name.given_name') ||
                                    safeGet(bookingData, 'paymentSource.name.full') ||
                                    safeGet(bookingData, 'payer.name.given_name') ||
                                    safeGet(bookingData, 'payer.name.surname') ||
                                    safeGet(bookingData, 'customer.name') ||
                                    safeGet(bookingData, 'user.displayName') ||
                                    safeGet(bookingData, 'name') ||
                                    '预约客户';
        }
        
        // 补充处理邮箱信息
        if (!bookingData.customerEmail) {
          bookingData.customerEmail = safeGet(bookingData, 'userInfo.email') ||
                                     safeGet(bookingData, 'userEmail') ||
                                     safeGet(bookingData, 'paymentSource.email_address') ||
                                     safeGet(bookingData, 'payer.email_address') ||
                                     safeGet(bookingData, 'customer.email') ||
                                     safeGet(bookingData, 'user.email') ||
                                     safeGet(bookingData, 'email') ||
                                     '';
        }
        
        // 补充处理电话信息
        if (!bookingData.customerPhone) {
          bookingData.customerPhone = safeGet(bookingData, 'userInfo.phone') ||
                                     safeGet(bookingData, 'phone') ||
                                     safeGet(bookingData, 'phoneNumber') ||
                                     safeGet(bookingData, 'customerPhoneNumber') ||
                                     safeGet(bookingData, 'customer.phone') ||
                                     safeGet(bookingData, 'user.phoneNumber') ||
                                     safeGet(bookingData, 'user.phone') ||
                                     '';
        }
        
        // 如果预约中没有价格信息，但有命理师ID，尝试从数据库中直接获取命理师价格
        if ((!bookingData.price || bookingData.price === 0) && 
            (!bookingData.amount && !bookingData.cost) && 
            bookingData.fortuneTellerId) {
          try {
            // 先检查本地缓存
            if (fortuneTellers[bookingData.fortuneTellerId]) {
              bookingData.price = fortuneTellers[bookingData.fortuneTellerId].price || 0;
            } else {
              // 直接从数据库获取
              const tellerDoc = await getDoc(doc(db, 'fortuneTellers', bookingData.fortuneTellerId));
              if (tellerDoc.exists()) {
                const tellerData = tellerDoc.data();
                bookingData.price = tellerData.price || 0;
                
                // 更新本地缓存
                setFortuneTellers(prev => ({
                  ...prev,
                  [bookingData.fortuneTellerId]: {
                    id: bookingData.fortuneTellerId,
                    ...tellerData
                  }
                }));
              }
            }
          } catch (err) {
            console.error('获取命理师价格失败:', err);
          }
        }
        
        // 处理支付相关信息
        if (!bookingData.paymentStatus) {
          bookingData.paymentStatus = safeGet(bookingData, 'paymentSource.status') ||
                                     safeGet(bookingData, 'payment.status') ||
                                     safeGet(bookingData, 'paymentDetails.status') ||
                                     'pending';
        }
        
        // 提取其他可能有用的信息
        if (!bookingData.paymentMethod) {
          bookingData.paymentMethod = safeGet(bookingData, 'payment.method') || 
                                     (bookingData.paymentSource ? 'PayPal' : null);
        }
        
        if (!bookingData.paymentDate) {
          bookingData.paymentDate = safeGet(bookingData, 'payment.date') || 
                                   safeGet(bookingData, 'paymentDetails.update_time') || 
                                   null;
        }
        
        if (!bookingData.transactionId) {
          bookingData.transactionId = safeGet(bookingData, 'payment.id') || 
                                     safeGet(bookingData, 'paymentDetails.id') || 
                                     null;
        }
        
        if (!bookingData.paymentId && bookingData.transactionId) {
          bookingData.paymentId = bookingData.transactionId;
        }
        
        // 提取预约主题和问题
        if (!bookingData.questions && bookingData.topic) {
          // 如果没有问题数组但有主题，将主题加入问题数组
          bookingData.questions = [bookingData.topic];
        }
        
        // 如果没有命理师名称，尝试获取
        if (!bookingData.fortuneTellerName) {
          bookingData.fortuneTellerName = safeGet(bookingData, 'service.providerName') ||
                                         safeGet(bookingData, 'provider.name') ||
                                         safeGet(bookingData, 'fortuneTeller.name') ||
                                         (fortuneTellers[bookingData.fortuneTellerId]?.name) ||
                                         '';
        }
        
        // 检查是否有用户数据，如果没有但有用户ID，尝试获取
        if (!bookingData.userData && bookingData.userId) {
          try {
            const userDoc = await getDoc(doc(db, 'users', bookingData.userId));
            if (userDoc.exists()) {
              bookingData.userData = userDoc.data();
            }
          } catch (error) {
            console.error(`获取用户数据失败: ${error.message}`);
          }
        }
        
        setSelectedBookingDetails(bookingData);
        setSelectedBooking(id);
        setOpenDialog(true);
        setDetailsTabValue(0); // 重置为第一个标签
      } else {
        setSnackbar({
          open: true,
          message: '找不到预约数据',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('加载预约数据失败:', error);
      setError('加载预约数据失败');
      setSnackbar({
        open: true,
        message: '加载预约数据失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Start status update process
  const handleStatusUpdateClick = (id, currentStatus, currentPaymentStatus) => {
    setSelectedBooking(id);
    setNewStatus(currentStatus);
    setNewPaymentStatus(currentPaymentStatus);
    setStatusUpdateDialog(true);
  };

  // Update booking status and send email notification
  const handleStatusUpdate = async () => {
    try {
      setLoading(true);
      
      // 获取当前预约的详细信息
      const bookingDoc = await getDoc(doc(db, 'bookings', selectedBooking));
      if (!bookingDoc.exists()) {
        throw new Error('预约不存在');
      }
      
      const bookingData = bookingDoc.data();
      
      // 获取用户信息
      let userEmail = null;
      let userName = null;
      
      if (bookingData.userId) {
        const userDoc = await getDoc(doc(db, 'users', bookingData.userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          userEmail = userData.email;
          userName = userData.displayName;
        }
      }
      
      // 更新预约状态
      const updateData = {
        status: newStatus,
        updatedAt: serverTimestamp()
      };
      
      // 如果支付状态也更新，添加到更新数据中
      if (newPaymentStatus !== bookingData.paymentStatus) {
        updateData.paymentStatus = newPaymentStatus;
      }
      
      await updateDoc(doc(db, 'bookings', selectedBooking), updateData);

      // 如果有用户邮箱，创建一个邮件通知
      if (userEmail) {
        // 获取状态中文名称
        const statusOption = statusOptions.find(option => option.value === newStatus);
        const statusName = statusOption ? statusOption.label : newStatus;
        
        const paymentStatusOption = paymentStatusOptions.find(option => option.value === newPaymentStatus);
        const paymentStatusName = paymentStatusOption ? paymentStatusOption.label : newPaymentStatus;
        
        // 获取占卜师名称和价格信息
        let fortuneTellerName = '占卜师';
        let fortuneTellerPrice = 0;
        
        if (bookingData.fortuneTellerId && fortuneTellers[bookingData.fortuneTellerId]) {
          fortuneTellerName = fortuneTellers[bookingData.fortuneTellerId].name;
          // 如果预约中没有价格信息，尝试从占卜师数据中获取
          if (!bookingData.price && !bookingData.amount && !bookingData.cost) {
            fortuneTellerPrice = fortuneTellers[bookingData.fortuneTellerId].price || 0;
            // 更新预约记录以包含价格
            updateData.price = fortuneTellerPrice;
          }
        }
        
        // 格式化预约日期
        const bookingDate = bookingData.date instanceof Date 
          ? bookingData.date.toLocaleDateString() 
          : new Date(bookingData.date.seconds * 1000).toLocaleDateString();
        
        // 格式化预约时间
        let bookingTime = '';
        if (bookingData.timeSlots && Array.isArray(bookingData.timeSlots)) {
          bookingTime = bookingData.timeSlots.join(', ');
        } else if (bookingData.timeSlot) {
          bookingTime = bookingData.timeSlot;
        }
        
        // 创建邮件通知文档
        const mailDoc = {
          to: [userEmail],
          message: {
            subject: `预约状态更新: ${userName || '用户'}的${fortuneTellerName}预约`,
            text: `您的预约状态已更新为: ${statusName}，支付状态: ${paymentStatusName}。\n\n预约日期: ${bookingDate}\n预约时间: ${bookingTime}\n\n感谢您的支持！`,
            html: `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <h2>预约状态更新</h2>
                <p>尊敬的${userName || '用户'}，您好！</p>
                <p>您预约的 <strong>${fortuneTellerName}</strong> 服务状态已更新:</p>
                <ul>
                  <li>预约状态: <strong>${statusName}</strong></li>
                  <li>支付状态: <strong>${paymentStatusName}</strong></li>
                  <li>预约日期: ${bookingDate}</li>
                  <li>预约时间: ${bookingTime}</li>
                </ul>
                <hr style="border: 1px solid #eee; margin: 20px 0;">
                <p>如有任何问题，请随时联系我们。</p>
                <p>感谢您的支持！</p>
              </div>
            `
          },
          createdAt: new Date()
        };
        
        // 保存到 mail 集合
        await setDoc(doc(collection(db, 'mail')), mailDoc);
        console.log('已创建邮件通知:', userEmail);
      }

      // Update local state
      setBookings(bookings.map(item => {
        if (item.id === selectedBooking) {
          return { 
            ...item, 
            status: newStatus,
            paymentStatus: newPaymentStatus !== item.paymentStatus ? newPaymentStatus : item.paymentStatus
          };
        }
        return item;
      }));

      setSnackbar({
        open: true,
        message: '预约状态已更新' + (userEmail ? '，并已发送邮件通知' : ''),
        severity: 'success',
      });
      
      setStatusUpdateDialog(false);
    } catch (error) {
      console.error('更新预约状态失败:', error);
      setError('更新预约状态失败');
      setSnackbar({
        open: true,
        message: '更新预约状态失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Confirm delete before proceeding
  const handleDeleteConfirm = (id) => {
    setSelectedBooking(id);
    setOpenDeleteDialog(true);
  };

  // Handle delete booking
  const handleDelete = async () => {
    try {
      setLoading(true);
      
      // Get booking details before deletion
      const bookingRef = doc(db, 'bookings', selectedBooking);
      const bookingDoc = await getDoc(bookingRef);
      
      if (!bookingDoc.exists()) {
        throw new Error('预约不存在');
      }
      
      const bookingData = bookingDoc.data();
      console.log(`删除预约前获取详情:`, bookingData);
      
      // Delete the booking document
      await deleteDoc(bookingRef);
      
      // Update local state
      setBookings(bookings.filter(item => item.id !== selectedBooking));
      
      setSnackbar({
        open: true,
        message: '预约已删除',
        severity: 'success',
      });
      
      setOpenDeleteDialog(false);
      
      // Add a log entry to record that a booking was deleted by admin
      try {
        const logEntry = {
          action: 'BOOKING_DELETED',
          bookingId: selectedBooking,
          fortuneTellerId: bookingData.fortuneTellerId,
          timeSlots: bookingData.timeSlots || [bookingData.timeSlot],
          formattedDate: bookingData.formattedDate,
          adminUid: 'admin', // Replace with actual admin ID if available
          timestamp: serverTimestamp()
        };
        
        await addDoc(collection(db, 'activityLogs'), logEntry);
        console.log('已记录预约删除活动');
      } catch (logError) {
        console.error('记录预约删除活动失败:', logError);
        // Continue with process even if logging fails
      }
    } catch (error) {
      console.error('删除预约失败:', error);
      setError('删除预约失败');
      setSnackbar({
        open: true,
        message: '删除预约失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
      setSelectedBooking(null);
    }
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };
  
  // Handle tab change in details dialog
  const handleTabChange = (event, newValue) => {
    setDetailsTabValue(newValue);
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (date instanceof Date) {
      return date.toLocaleString('zh-CN');
    }
    if (date.toDate) {
      return date.toDate().toLocaleString('zh-CN');
    }
    if (date.seconds) {
      return new Date(date.seconds * 1000).toLocaleString('zh-CN');
    }
    return new Date(date).toLocaleString('zh-CN');
  };

  // Format price
  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'S$0.00';
    
    // Convert to number if it's a string
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    
    // Check if conversion resulted in a valid number
    if (isNaN(numericPrice)) return 'S$0.00';
    
    return `S$${numericPrice.toFixed(2)}`;
  };

  // Format time slots
  const formatTimeSlots = (timeSlots) => {
    if (Array.isArray(timeSlots)) {
      return timeSlots.join(', ');
    }
    return timeSlots || 'N/A';
  };

  // Get status chip
  const getStatusChip = (status, paymentStatus) => {
    // 首先检查支付状态
    if (paymentStatus === 'pending' || paymentStatus === 'PENDING') {
      return <Chip label="待支付" color="warning" size="small" />;
    } else if (paymentStatus === 'completed' || paymentStatus === 'COMPLETED') {
      if (status === 'completed' || status === 'COMPLETED') {
        return <Chip label="已完成" color="success" size="small" />;
      } else if (status === 'cancelled' || status === 'CANCELLED') {
        return <Chip label="已取消" color="error" size="small" />;
      } else if (status === 'confirmed' || status === 'CONFIRMED') {
        return <Chip label="已确认" color="info" size="small" />;
      }
      return <Chip label="已支付" color="success" size="small" />;
    } else if (paymentStatus === 'refunded' || paymentStatus === 'REFUNDED') {
      return <Chip label="已退款" color="error" size="small" />;
    }
    
    // 如果支付状态为空，则根据预约状态显示
    switch (status) {
      case 'pending': return <Chip label="待确认" color="warning" size="small" />;
      case 'confirmed': return <Chip label="已确认" color="info" size="small" />;
      case 'completed': return <Chip label="已完成" color="success" size="small" />;
      case 'cancelled': return <Chip label="已取消" color="error" size="small" />;
      default: return <Chip label={status || '未知'} color="default" size="small" />;
    }
  };

  if (loading && bookings.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ my: 4 }}>
      {/* Toolbar */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <TextField
            label="搜索预约"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} edge="end">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel>状态</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="状态"
            >
              <MenuItem value="all">全部</MenuItem>
              <MenuItem value="paid">已付款</MenuItem>
              <MenuItem value="unpaid">未付款</MenuItem>
              {statusOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            startIcon={<RefreshIcon />}
            onClick={() => loadBookings(true)}
            color="primary"
            variant="outlined"
            size="medium"
          >
            刷新
          </Button>
        </Box>
      </Box>

      {/* Bookings table */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="预约列表">
          <TableHead>
            <TableRow>
              <TableCell>预约ID</TableCell>
              <TableCell>日期</TableCell>
              <TableCell>时间</TableCell>
              <TableCell>客户</TableCell>
              <TableCell>联系电话</TableCell>
              <TableCell>占卜师</TableCell>
              <TableCell align="right">金额</TableCell>
              <TableCell align="center">状态</TableCell>
              <TableCell align="center">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && bookings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : bookings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  暂无数据
                </TableCell>
              </TableRow>
            ) : (
              bookings.map((booking) => (
                <TableRow key={booking.id}>
                  <TableCell>
                    <Typography variant="body2">{booking.id.substring(0, 10)}...</Typography>
                  </TableCell>
                  <TableCell>
                    {formatDate(booking.date).split(' ')[0]}
                  </TableCell>
                  <TableCell>
                    {booking.timeSlots 
                      ? formatTimeSlots(booking.timeSlots)
                      : formatTimeSlots(booking.timeSlot)}
                  </TableCell>
                  <TableCell>
                    {booking.customerName || 'N/A'}
                    {booking.customerEmail && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {booking.customerEmail}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {booking.customerPhone || 'N/A'}
                  </TableCell>
                  <TableCell>
                    {fortuneTellers[booking.fortuneTellerId]?.name || '未知占卜师'}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {formatPrice(
                        booking.price || 
                        booking.amount || 
                        booking.cost || 
                        (fortuneTellers[booking.fortuneTellerId]?.price) || 
                        0
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {getStatusChip(booking.status, booking.paymentStatus)}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title="查看详情">
                        <IconButton
                          color="info"
                          onClick={() => handleViewBooking(booking.id)}
                          size="small"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      
                      {/* 已确认按钮 */}
                      <Tooltip title="设为已确认">
                        <IconButton
                          color={booking.status === 'confirmed' ? "info" : "default"}
                          onClick={() => handleStatusUpdateClick(booking.id, 'confirmed', booking.paymentStatus)}
                          size="small"
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      </Tooltip>
                      
                      {/* 已付款按钮 */}
                      <Tooltip title="设为已付款">
                        <IconButton
                          color={booking.paymentStatus === 'completed' ? "success" : "default"}
                          onClick={() => handleStatusUpdateClick(booking.id, booking.status, 'completed')}
                          size="small"
                        >
                          <PaymentIcon />
                        </IconButton>
                      </Tooltip>
                      
                      {/* 已完成按钮 */}
                      <Tooltip title="设为已完成">
                        <IconButton
                          color={booking.status === 'completed' ? "success" : "default"}
                          onClick={() => handleStatusUpdateClick(booking.id, 'completed', booking.paymentStatus)}
                          size="small"
                        >
                          <ShippingIcon />
                        </IconButton>
                      </Tooltip>
                      
                      {/* 已取消按钮 */}
                      <Tooltip title="设为已取消">
                        <IconButton
                          color={booking.status === 'cancelled' ? "error" : "default"}
                          onClick={() => handleStatusUpdateClick(booking.id, 'cancelled', booking.paymentStatus)}
                          size="small"
                        >
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Load more button */}
      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            onClick={() => loadBookings(false)}
            disabled={loadingMore}
            variant="outlined"
            startIcon={loadingMore ? <CircularProgress size={20} /> : null}
          >
            {loadingMore ? '加载中...' : '加载更多'}
          </Button>
        </Box>
      )}

      {/* Booking details dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">预约详情 - ID: {selectedBooking}</Typography>
            {selectedBookingDetails && getStatusChip(selectedBookingDetails.status, selectedBookingDetails.paymentStatus)}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedBookingDetails && (
            <>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={detailsTabValue} onChange={handleTabChange} aria-label="预约详情标签">
                  <Tab label="基本信息" id="tab-0" />
                  <Tab label="客户信息" id="tab-1" />
                  <Tab label="支付详情" id="tab-2" />
                  <Tab label="预约内容" id="tab-3" />
                </Tabs>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <FormControlLabel
                  control={<Switch checked={showRawData} onChange={(e) => setShowRawData(e.target.checked)} />}
                  label="显示原始数据"
                />
              </Box>
              
              {/* 基本信息 Tab */}
              {detailsTabValue === 0 && (
                <Grid container spacing={3}>
                  {/* Booking Summary */}
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6">
                            预约概览
                          </Typography>
                        </Box>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              <strong>预约编号:</strong> {selectedBooking}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              <strong>预约日期:</strong> {formatDate(selectedBookingDetails.date)}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              <strong>预约时间:</strong> {selectedBookingDetails.timeSlots 
                                ? formatTimeSlots(selectedBookingDetails.timeSlots)
                                : formatTimeSlots(selectedBookingDetails.timeSlot)}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              <strong>金额:</strong> {formatPrice(
                                selectedBookingDetails.price || 
                                selectedBookingDetails.amount || 
                                selectedBookingDetails.cost || 
                                (fortuneTellers[selectedBookingDetails.fortuneTellerId]?.price) || 
                                0
                              )}
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              <strong>预约状态:</strong> {
                                selectedBookingDetails.status === 'pending' ? '待确认' :
                                selectedBookingDetails.status === 'confirmed' ? '已确认' :
                                selectedBookingDetails.status === 'completed' ? '已完成' :
                                selectedBookingDetails.status === 'cancelled' ? '已取消' :
                                selectedBookingDetails.status || 'N/A'
                              }
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              <strong>支付状态:</strong> {
                                selectedBookingDetails.paymentStatus === 'pending' ? '待付款' :
                                selectedBookingDetails.paymentStatus === 'completed' ? '已付款' :
                                selectedBookingDetails.paymentStatus === 'refunded' ? '已退款' :
                                selectedBookingDetails.paymentStatus || 'N/A'
                              }
                            </Typography>
                            {selectedBookingDetails.createdAt && (
                              <Typography variant="body2" color="textSecondary" gutterBottom>
                                <strong>创建时间:</strong> {formatDate(selectedBookingDetails.createdAt)}
                              </Typography>
                            )}
                            {selectedBookingDetails.updatedAt && (
                              <Typography variant="body2" color="textSecondary" gutterBottom>
                                <strong>最后更新:</strong> {formatDate(selectedBookingDetails.updatedAt)}
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Fortune Teller Info */}
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          占卜师信息
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body1" gutterBottom>
                              <strong>名称:</strong> {fortuneTellers[selectedBookingDetails.fortuneTellerId]?.name || 
                                                   selectedBookingDetails.fortuneTellerName || '未知占卜师'}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              <strong>ID:</strong> {selectedBookingDetails.fortuneTellerId || 'N/A'}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              <strong>价格:</strong> <span style={{ color: '#1976d2', fontWeight: 'bold' }}>
                                {formatPrice(
                                  selectedBookingDetails.price || 
                                  selectedBookingDetails.amount || 
                                  selectedBookingDetails.cost || 
                                  (fortuneTellers[selectedBookingDetails.fortuneTellerId]?.price) || 
                                  0
                                )}
                              </span>
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body1" gutterBottom>
                              <strong>专长:</strong> {fortuneTellers[selectedBookingDetails.fortuneTellerId]?.specialties?.join(', ') || 'N/A'}
                            </Typography>
                            {fortuneTellers[selectedBookingDetails.fortuneTellerId]?.availability && (
                              <Typography variant="body1" gutterBottom>
                                <strong>可用时间:</strong> {
                                  typeof fortuneTellers[selectedBookingDetails.fortuneTellerId]?.availability === 'string'
                                    ? fortuneTellers[selectedBookingDetails.fortuneTellerId]?.availability
                                    : JSON.stringify(fortuneTellers[selectedBookingDetails.fortuneTellerId]?.availability)
                                }
                              </Typography>
                            )}
                            {fortuneTellers[selectedBookingDetails.fortuneTellerId]?.rating && (
                              <Typography variant="body1" gutterBottom>
                                <strong>评分:</strong> {fortuneTellers[selectedBookingDetails.fortuneTellerId]?.rating}
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
              
              {/* 客户信息 Tab */}
              {detailsTabValue === 1 && (
                <Grid container spacing={3}>
                  {/* Customer Info */}
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          客户信息
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body1" gutterBottom>
                              <strong>姓名:</strong> {selectedBookingDetails.customerName || 'N/A'}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              <strong>电子邮箱:</strong> <span style={{ color: '#1976d2' }}>
                                {selectedBookingDetails.customerEmail || 
                                 (selectedBookingDetails.userInfo && selectedBookingDetails.userInfo.email) ||
                                 (selectedBookingDetails.userData && selectedBookingDetails.userData.email) ||
                                 (selectedBookingDetails.paymentSource && selectedBookingDetails.paymentSource.email_address) ||
                                 'N/A'}
                              </span>
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              <strong>电话:</strong> {selectedBookingDetails.customerPhone || 
                                                   (selectedBookingDetails.userInfo && selectedBookingDetails.userInfo.phone) ||
                                                   (selectedBookingDetails.userData && (selectedBookingDetails.userData.phoneNumber || selectedBookingDetails.userData.phone)) ||
                                                   'N/A'}
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            {selectedBookingDetails.userId && (
                              <Typography variant="body1" gutterBottom>
                                <strong>用户ID:</strong> {selectedBookingDetails.userId}
                              </Typography>
                            )}
                            {selectedBookingDetails.address && (
                              <Typography variant="body1" gutterBottom>
                                <strong>地址:</strong> {
                                  typeof selectedBookingDetails.address === 'string'
                                    ? selectedBookingDetails.address
                                    : JSON.stringify(selectedBookingDetails.address)
                                }
                              </Typography>
                            )}
                            {selectedBookingDetails.country && (
                              <Typography variant="body1" gutterBottom>
                                <strong>国家:</strong> {selectedBookingDetails.country}
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                        
                        {/* 额外的客户数据展示 */}
                        {selectedBookingDetails.userData && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              用户档案信息
                            </Typography>
                            <Divider sx={{ mb: 1 }} />
                            <Grid container spacing={2}>
                              {Object.entries(selectedBookingDetails.userData).map(([key, value]) => {
                                // 过滤掉敏感或冗余信息
                                if (key === 'password' || key === 'salt' || key === '_id' || key === '__v') return null;
                                
                                const displayValue = typeof value === 'object' && value !== null
                                  ? JSON.stringify(value)
                                  : String(value);
                                  
                                return (
                                  <Grid item xs={12} sm={6} key={key}>
                                    <Typography variant="body2" gutterBottom>
                                      <strong>{key}:</strong> {displayValue}
                                    </Typography>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
              
              {/* 支付详情 Tab */}
              {detailsTabValue === 2 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          支付信息
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body1" gutterBottom>
                              <strong>支付状态:</strong> {
                                selectedBookingDetails.paymentStatus === 'pending' ? '待付款' :
                                selectedBookingDetails.paymentStatus === 'completed' ? '已付款' :
                                selectedBookingDetails.paymentStatus === 'refunded' ? '已退款' :
                                selectedBookingDetails.paymentStatus || 'N/A'
                              }
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              <strong>支付金额:</strong> <span style={{ fontWeight: 'bold' }}>
                                {formatPrice(
                                  selectedBookingDetails.price || 
                                  selectedBookingDetails.amount || 
                                  selectedBookingDetails.cost || 
                                  (fortuneTellers[selectedBookingDetails.fortuneTellerId]?.price) || 
                                  0
                                )}
                              </span>
                            </Typography>
                            {selectedBookingDetails.paymentMethod && (
                              <Typography variant="body1" gutterBottom>
                                <strong>支付方式:</strong> {
                                selectedBookingDetails.paymentMethod === 'paypal_server' 
                                  ? 'PayPal(服务器)' 
                                  : selectedBookingDetails.paymentMethod
                              }
                              </Typography>
                            )}
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            {selectedBookingDetails.paymentId && (
                              <Typography variant="body1" gutterBottom>
                                <strong>支付ID:</strong> {selectedBookingDetails.paymentId}
                              </Typography>
                            )}
                            {selectedBookingDetails.transactionId && (
                              <Typography variant="body1" gutterBottom>
                                <strong>交易ID:</strong> {selectedBookingDetails.transactionId}
                              </Typography>
                            )}
                            {selectedBookingDetails.paymentDate && (
                              <Typography variant="body1" gutterBottom>
                                <strong>支付日期:</strong> {formatDate(selectedBookingDetails.paymentDate)}
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                        
                        {/* PayPal支付源信息 */}
                        {selectedBookingDetails.paymentSource && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              PayPal支付源信息
                            </Typography>
                            <Divider sx={{ mb: 1 }} />
                            
                            <Grid container spacing={2}>
                              {selectedBookingDetails.paymentSource.email_address && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" gutterBottom>
                                    <strong>电子邮箱:</strong> {selectedBookingDetails.paymentSource.email_address}
                                  </Typography>
                                </Grid>
                              )}
                              
                              {selectedBookingDetails.paymentSource.name && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" gutterBottom>
                                    <strong>姓名:</strong> {
                                      `${selectedBookingDetails.paymentSource.name.given_name || ''} ${selectedBookingDetails.paymentSource.name.surname || ''}`.trim() || 'N/A'
                                    }
                                  </Typography>
                                </Grid>
                              )}
                              
                              {selectedBookingDetails.paymentSource.account_id && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" gutterBottom>
                                    <strong>账户ID:</strong> {selectedBookingDetails.paymentSource.account_id}
                                  </Typography>
                                </Grid>
                              )}
                              
                              {selectedBookingDetails.paymentSource.status && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" gutterBottom>
                                    <strong>状态:</strong> {selectedBookingDetails.paymentSource.status}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>
                            
                            {/* 显示其他PayPal源信息 */}
                            {Object.entries(selectedBookingDetails.paymentSource)
                              .filter(([key]) => !['email_address', 'name', 'account_id', 'status'].includes(key))
                              .map(([key, value]) => {
                                const displayValue = typeof value === 'object' && value !== null
                                  ? JSON.stringify(value)
                                  : String(value);
                                  
                                return (
                                  <Typography variant="body2" gutterBottom key={key}>
                                    <strong>{key}:</strong> {displayValue}
                                  </Typography>
                                );
                              })
                            }
                          </Box>
                        )}
                        
                        {/* PayPal支付详情 */}
                        {selectedBookingDetails.paymentDetails && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              支付详情
                            </Typography>
                            
                            {/* 检查是否是服务器端支付，显示更友好的UI */}
                            {selectedBookingDetails.paymentMethod === 'paypal_server' && 
                             selectedBookingDetails.paymentDetails.captureDetails && (
                              <Box sx={{ mb: 2 }}>
                                <Card variant="outlined" sx={{ mb: 2 }}>
                                  <CardContent>
                                    {selectedBookingDetails.paymentDetails.captureDetails.payer && (
                                      <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                          付款人信息
                                        </Typography>
                                        <Divider sx={{ mb: 1 }} />
                                        
                                        {selectedBookingDetails.paymentDetails.captureDetails.payer.email_address && (
                                          <Typography variant="body2" gutterBottom>
                                            <strong>邮箱:</strong> {selectedBookingDetails.paymentDetails.captureDetails.payer.email_address}
                                          </Typography>
                                        )}
                                        
                                        {selectedBookingDetails.paymentDetails.captureDetails.payer.payer_id && (
                                          <Typography variant="body2" gutterBottom>
                                            <strong>PayPal ID:</strong> {selectedBookingDetails.paymentDetails.captureDetails.payer.payer_id}
                                          </Typography>
                                        )}
                                        
                                        {selectedBookingDetails.paymentDetails.captureDetails.payer.name && (
                                          <Typography variant="body2" gutterBottom>
                                            <strong>姓名:</strong> {selectedBookingDetails.paymentDetails.captureDetails.payer.name.given_name} {selectedBookingDetails.paymentDetails.captureDetails.payer.name.surname}
                                          </Typography>
                                        )}
                                      </Box>
                                    )}
                                    
                                    {selectedBookingDetails.paymentDetails.captureDetails.amount && (
                                      <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                          支付金额
                                        </Typography>
                                        <Divider sx={{ mb: 1 }} />
                                        <Typography variant="body2" gutterBottom color="primary.main" fontWeight="bold">
                                          {selectedBookingDetails.paymentDetails.captureDetails.amount.value} {selectedBookingDetails.paymentDetails.captureDetails.amount.currency_code}
                                        </Typography>
                                      </Box>
                                    )}
                                    
                                    <Typography variant="subtitle2" gutterBottom>
                                      交易信息
                                    </Typography>
                                    <Divider sx={{ mb: 1 }} />
                                    <Typography variant="body2" gutterBottom>
                                      <strong>状态:</strong> {selectedBookingDetails.paymentDetails.captureDetails.status}
                                    </Typography>
                                    <Typography variant="body2" gutterBottom>
                                      <strong>捕获ID:</strong> {selectedBookingDetails.paymentDetails.captureDetails.id}
                                    </Typography>
                                    <Typography variant="body2" gutterBottom>
                                      <strong>创建时间:</strong> {selectedBookingDetails.paymentDetails.captureDetails.create_time}
                                    </Typography>
                                    <Typography variant="body2" gutterBottom>
                                      <strong>更新时间:</strong> {selectedBookingDetails.paymentDetails.captureDetails.update_time}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              </Box>
                            )}
                            
                            <pre style={{ 
                              background: '#f5f5f5', 
                              padding: '8px', 
                              borderRadius: '4px',
                              overflow: 'auto',
                              maxHeight: '200px'
                            }}>
                              {JSON.stringify(selectedBookingDetails.paymentDetails, null, 2)}
                            </pre>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
              
              {/* 预约内容 Tab */}
              {detailsTabValue === 3 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          预约内容
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        {selectedBookingDetails.topic && (
                          <Typography variant="body1" gutterBottom>
                            <strong>咨询主题:</strong> {selectedBookingDetails.topic}
                          </Typography>
                        )}
                        
                        {selectedBookingDetails.service && selectedBookingDetails.service.name && (
                          <Typography variant="body1" gutterBottom>
                            <strong>服务名称:</strong> {selectedBookingDetails.service.name}
                          </Typography>
                        )}
                        
                        {selectedBookingDetails.service && selectedBookingDetails.service.description && (
                          <Typography variant="body1" gutterBottom>
                            <strong>服务描述:</strong> {selectedBookingDetails.service.description}
                          </Typography>
                        )}
                        
                        {selectedBookingDetails.duration && (
                          <Typography variant="body1" gutterBottom>
                            <strong>服务时长:</strong> {selectedBookingDetails.duration} 分钟
                          </Typography>
                        )}
                        
                        {selectedBookingDetails.notes && (
                          <Typography variant="body1" gutterBottom>
                            <strong>备注:</strong> {selectedBookingDetails.notes}
                          </Typography>
                        )}
                        
                        {selectedBookingDetails.response && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              占卜结果/回复
                            </Typography>
                            <Card variant="outlined">
                              <CardContent>
                                <Typography variant="body2">
                                  {selectedBookingDetails.response}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Box>
                        )}
                        
                        {selectedBookingDetails.questions && Array.isArray(selectedBookingDetails.questions) && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              预约问题
                            </Typography>
                            <List>
                              {selectedBookingDetails.questions.map((question, index) => (
                                <ListItem key={index} divider>
                                  <ListItemText 
                                    primary={`问题 ${index + 1}`} 
                                    secondary={typeof question === 'string' ? question : JSON.stringify(question)}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                        
                        {selectedBookingDetails.history && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              预约历史记录
                            </Typography>
                            <pre style={{ 
                              background: '#f5f5f5', 
                              padding: '8px', 
                              borderRadius: '4px',
                              overflow: 'auto',
                              maxHeight: '200px'
                            }}>
                              {JSON.stringify(selectedBookingDetails.history, null, 2)}
                            </pre>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
              
              {/* 原始数据显示 */}
              {showRawData && (
                <Box sx={{ mt: 3 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        原始数据（调试用）
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <pre style={{ 
                        background: '#f5f5f5', 
                        padding: '8px', 
                        borderRadius: '4px',
                        overflow: 'auto',
                        maxHeight: '400px'
                      }}>
                        {JSON.stringify(selectedBookingDetails, null, 2)}
                      </pre>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>关闭</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              setOpenDialog(false);
              handleStatusUpdateClick(
                selectedBooking, 
                selectedBookingDetails.status, 
                selectedBookingDetails.paymentStatus
              );
            }}
          >
            更新状态
          </Button>
          <Button
            color="error"
            variant="outlined"
            onClick={() => {
              setOpenDialog(false);
              handleDeleteConfirm(selectedBooking);
            }}
          >
            删除预约
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status update dialog */}
      <Dialog
        open={statusUpdateDialog}
        onClose={() => setStatusUpdateDialog(false)}
      >
        <DialogTitle>更新预约状态</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            请选择新的预约状态和支付状态:
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>预约状态</InputLabel>
                <Select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  label="预约状态"
                >
                  {statusOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>支付状态</InputLabel>
                <Select
                  value={newPaymentStatus}
                  onChange={(e) => setNewPaymentStatus(e.target.value)}
                  label="支付状态"
                >
                  {paymentStatusOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusUpdateDialog(false)}>取消</Button>
          <Button
            onClick={handleStatusUpdate}
            color="primary"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            更新
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            确定要删除这个预约吗？此操作不可撤销。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>取消</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            删除
          </Button>
        </DialogActions>
      </Dialog>

      {/* Global notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BookingManagement;